/**
 * FR-015-147, `contracts/client-api.md` § Two credentials, one rule: the one seam below
 * header formation, problem parsing, retry, idempotency and uploads. `@baton/api` calls
 * `transport(request)` and defaults to the platform `fetch` when nothing is installed, so
 * the browser configures nothing; feature 017 installs one that routes through the Tauri
 * shell and feature 016 one over the native fetch and cookie store.
 */
export type Transport = (request: Request, observer?: TransportObserver) => Promise<Response>;

/**
 * What a caller watches of one request while it is sent (018 FR-018-395's upload progress,
 * 2026-10-21): how many bytes of its body have gone, of how many. A transport that cannot see its
 * body leave says nothing, and the caller shows the send as under way without a share.
 */
export interface TransportObserver {
  readonly onUploadProgress?: (loaded: number, total: number) => void;
}

/**
 * The default: the platform `fetch`, looked up at call time so a test can stub it. `fetch` reports
 * nothing of a body being sent, so a request whose upload is watched goes through `XMLHttpRequest`
 * where the platform has one, which does; the request, its headers, its credentials and its signal
 * are the same, and the answer is read back into a `Response`. That sender is a module of its own
 * (`watched.ts`), loaded on the first watched request, so the documents' first load carries none of it.
 */
export const platformFetch: Transport = (request, observer) => {
  const onUploadProgress = observer?.onUploadProgress;
  if (onUploadProgress === undefined || typeof XMLHttpRequest === "undefined") return fetch(request);
  return import("./watched.ts").then(({ sendWatched }) => sendWatched(request, onUploadProgress));
};

let installed: Transport | null = null;

/** Install a transport for the whole client; the returned function removes it again, and
 * removing it restores the platform `fetch`. Installing a second one replaces the first. */
export function installTransport(transport: Transport): () => void {
  installed = transport;
  return () => {
    if (installed === transport) installed = null;
  };
}

/** The transport every request goes through: the installed one, or the platform `fetch`. */
export function currentTransport(): Transport {
  return installed ?? platformFetch;
}

export function hasInstalledTransport(): boolean {
  return installed !== null;
}
